/**
 * API 클라이언트 — Express API(3003) 호출 래퍼
 *
 * next.config.ts의 rewrites로 /api/* → Express 프록시되므로
 * 클라이언트에서는 상대 경로로 호출하면 됩니다.
 *
 * 서버 컴포넌트에서는 API_BASE_URL 환경변수를 사용합니다.
 *
 * 변경 요청(POST/PUT/DELETE/PATCH)에는 CSRF 토큰이 자동 포함됩니다.
 */

import { getCsrfToken, invalidateCsrfToken, CSRF_HEADER } from "./csrf";
import {biteCountHeader} from './bite-conversion';

const isServer = typeof window === "undefined";

/** 서버 사이드에서 사용할 Express API 베이스 URL */
const API_BASE = isServer
  ? process.env.API_BASE_URL || "http://127.0.0.1:3005"
  : "";

/** API 에러 */
export class ApiError extends Error {
  /** 서버 응답 JSON에서 추출한 추가 데이터 (code, isSubscriber 등) */
  public data?: Record<string, unknown>;

  constructor(
    public status: number,
    public statusText: string,
    message?: string,
    data?: Record<string, unknown>,
  ) {
    super(message || `API Error: ${status} ${statusText}`);
    this.name = "ApiError";
    this.data = data;
  }
}

/** fetch 래퍼 옵션 */
interface ApiOptions extends Omit<RequestInit, "body"> {
  /** JSON body (자동 직렬화) */
  body?: unknown;
  /** 쿠키 전달 (서버 사이드 fetch 시 필요) */
  cookie?: string;
}

/**
 * API fetch 래퍼
 *
 * @example
 * // 클라이언트
 * const data = await api<UserData>("/api/me");
 *
 * // 서버 컴포넌트
 * const data = await api<UserData>("/api/me", { cookie: cookies().toString() });
 *
 * // POST
 * const result = await api("/api/ticker/analyze", {
 *   method: "POST",
 *   body: { ticker: "AAPL", market: "us" },
 * });
 */
/** CSRF 토큰이 필요한 HTTP 메서드 */
const MUTATING_METHODS = new Set(["POST", "PUT", "DELETE", "PATCH"]);

export async function api<T = unknown>(
  path: string,
  options: ApiOptions = {},
): Promise<T> {
  const { body, cookie, ...fetchOptions } = options;
  const method = (fetchOptions.method || "GET").toUpperCase();

  const url = `${API_BASE}${path}`;

  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(fetchOptions.headers as Record<string, string>),
  };
  if(!isServer && (/^\/api\/ticker\/bite\/prepare(?:\/|$)/.test(path)||path==='/api/bite/stripe/checkout'||path==='/api/ticker/bite/cards/open')) {
    const count=biteCountHeader();if(count)headers['x-bite-count']=count;
  }

  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
  }

  if (cookie) {
    headers["Cookie"] = cookie;
  }

  // 클라이언트 사이드 변경 요청에 CSRF 토큰 자동 주입
  if (!isServer && MUTATING_METHODS.has(method) && !headers[CSRF_HEADER]) {
    const token = await getCsrfToken();
    if (token) {
      headers[CSRF_HEADER] = token;
    }
  }

  const res = await fetch(url, {
    ...fetchOptions,
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    credentials: isServer ? undefined : "same-origin",
  });

  // 403 CSRF 토큰 만료 시 1회 재시도
  if (!isServer && res.status === 403 && MUTATING_METHODS.has(method)) {
    const errBody = await res.json().catch(() => ({}));
    const errMsg = (errBody as { error?: string }).error || "";
    if (errMsg.includes("CSRF")) {
      invalidateCsrfToken();
      const newToken = await getCsrfToken();
      if (newToken) {
        headers[CSRF_HEADER] = newToken;
      }
      const retryRes = await fetch(url, {
        ...fetchOptions,
        method,
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
        credentials: "same-origin",
      });
      return handleResponse<T>(retryRes);
    }
  }

  return handleResponse<T>(res);
}

/** 응답 파싱 공통 처리 */
async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let message: string | undefined;
    let data: Record<string, unknown> | undefined;
    try {
      const errBody = await res.json();
      message = errBody.error || errBody.message;
      data = errBody;
    } catch {
      // 응답 파싱 실패 시 기본 에러 메시지 사용
    }
    throw new ApiError(res.status, res.statusText, message, data);
  }

  // 204 No Content 등 빈 응답 처리
  const contentType = res.headers.get("content-type");
  if (!contentType || !contentType.includes("application/json")) {
    return undefined as T;
  }

  return res.json() as Promise<T>;
}

/**
 * SSE 스트림 URL 생성 헬퍼
 * 클라이언트에서 EventSource에 사용
 */
export function sseUrl(path: string): string {
  return `${API_BASE}${path}`;
}
